import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { LineBillingSeriesPoint, useLineBillingDetail, useLineBillingPeriods, useLineBillingSeries } from '@/hooks/useLineBilling';
import { fadeUp, stagger } from '@/lib/animations';
import { cn } from '@/lib/utils';
import type { ConsumptionUnit, LineBillingDetail, LineConsumptionBillingRow, LineSubscriptionBillingRow } from '@octave/contracts';
import { motion } from 'framer-motion';
import { BarChart3, ReceiptEuro, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { formatDateShort, formatDecimal, formatMonthYear, formatMonthYearShort, formatNumber } from '@/lib/format';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from 'recharts';

const eur = (amount: number) => `${formatDecimal(amount)} €`;

const formatDay = (iso: string) =>
  formatDateShort(iso);

const monthLabelOf = (month: number, year: number) =>
  formatMonthYear(month, year);

/** Compact axis label, e.g. « janv. 26 ». */
const monthShortOf = (month: number, year: number) =>
  formatMonthYearShort(month, year);

const eurAxis = (amount: number) => `${formatNumber(Math.round(amount))} €`;

const periodKey = (p: { month: number; year: number }) => `${p.year}-${p.month}`;

/** Raw provider units → readable display (voice in min, data in Mo, else count). */
const formatConsumptionValue = (unit: ConsumptionUnit, value: number) => {
  switch (unit) {
    case 'SECONDS':
      return value < 60
        ? `${Math.round(value)} s`
        : `${Math.round(value / 60)} min`;
    case 'OCTETS':
      return `${(value / 1024 / 1024).toFixed(1)} Mo`;
    case 'QTE':
      return formatNumber(value);
  }
};

const sumAmount = (rows: { amount: number }[]) =>
  rows.reduce((acc, r) => acc + r.amount, 0);

export function LineBillingTab({ lineId }: { lineId: string | undefined }) {
  const { data: periods } = useLineBillingPeriods(lineId);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Default to the line's latest billed period; selecting another browses history.
  const selected =
    periods?.find((p) => periodKey(p) === selectedKey) ?? periods?.[0] ?? null;

  const { data, isLoading, isError } = useLineBillingDetail(
    lineId,
    selected?.month,
    selected?.year
  );

  const monthLabel =
    data && data.hasData
      ? monthLabelOf(data.month, data.year)
      : selected
        ? monthLabelOf(selected.month, selected.year)
        : '';

  // Chips ordered oldest → most recent (left to right).
  const orderedPeriods = periods
    ? [...periods].sort((a, b) => a.year - b.year || a.month - b.month)
    : [];

  const selector = orderedPeriods.length > 1 && selected && (
    <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
      {orderedPeriods.map((p) => {
        const active = periodKey(p) === periodKey(selected);
        return (
          <button
            key={periodKey(p)}
            type="button"
            onClick={() => setSelectedKey(periodKey(p))}
            className={cn(
              'shrink-0 cursor-pointer rounded-full px-3.5 py-1.5 text-xs font-medium capitalize transition-colors',
              active
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'bg-white text-slate-600 shadow-sm hover:bg-slate-50'
            )}
          >
            {monthLabelOf(p.month, p.year)}
          </button>
        );
      })}
    </div>
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Skeleton className="h-72 rounded-2xl" />
        <Skeleton className="h-72 rounded-2xl" />
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  } else if (isError) {
    body = (
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <p className="py-8 text-center text-sm text-slate-400">
          Données de facturation indisponibles pour le moment
        </p>
      </div>
    );
  } else if (!data || !data.hasData) {
    body = (
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-50">
            <ReceiptEuro className="h-5 w-5 text-slate-400" />
          </div>
          <p className="text-sm font-medium text-slate-600">
            Aucune facturation disponible pour cette ligne
          </p>
          <p className="text-xs text-slate-400">
            Le détail apparaîtra une fois la facturation du mois publiée.
          </p>
        </div>
      </div>
    );
  } else {
    body = <BillingCards data={data} monthLabel={monthLabel} />;
  }

  return (
    <div className="space-y-4">
      {selector}
      {body}
      <BillingTrendChart lineId={lineId} />
    </div>
  );
}

const trendConfig = {
  subscriptionTotal: { label: 'Abonnement', color: '#10b981' },
  consumptionTotal: { label: 'Hors forfait', color: '#0ea5e9' },
} satisfies ChartConfig;

/** Trend of abonnement + hors-forfait across the line's billed months (bars or lines). */
function BillingTrendChart({ lineId }: { lineId: string | undefined }) {
  const { series } = useLineBillingSeries(lineId);
  const [chartType, setChartType] = useState<'bar' | 'curve'>('bar');

  // A single month already reads off the cards above — only chart a real trend.
  if (series.length < 2) return null;

  const data = series.map((p: LineBillingSeriesPoint) => ({
    ...p,
    label: monthShortOf(p.month, p.year),
  }));

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className="rounded-2xl bg-white p-6 shadow-sm"
    >
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Évolution</h2>
          <p className="mt-0.5 text-sm text-slate-400">
            Abonnement et hors-forfait mois par mois
          </p>
        </div>
        <div className="flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5">
          <button
            type="button"
            onClick={() => setChartType('bar')}
            className={cn(
              'rounded-md p-1.5 transition-colors',
              chartType === 'bar'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-400 hover:text-slate-600'
            )}
          >
            <BarChart3 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setChartType('curve')}
            className={cn(
              'rounded-md p-1.5 transition-colors',
              chartType === 'curve'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-400 hover:text-slate-600'
            )}
          >
            <TrendingUp className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="mt-5 h-[300px] w-full">
        <ChartContainer config={trendConfig} className="h-full w-full">
          <ComposedChart
            accessibilityLayer
            data={data}
            margin={{ top: 16, right: 12, left: 4, bottom: 0 }}
            barGap={2}
            barCategoryGap="30%"
          >
            <CartesianGrid vertical={false} stroke="#f1f5f9" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              tickMargin={8}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              tickMargin={4}
              tickFormatter={eurAxis}
              width={56}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  indicator="dot"
                  formatter={(value, name, item) => (
                    <div className="flex w-full items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-[2px]"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="flex flex-1 items-center justify-between gap-3">
                        <span className="text-muted-foreground">
                          {trendConfig[name as keyof typeof trendConfig]
                            ?.label ?? name}
                        </span>
                        <span className="font-mono font-medium tabular-nums text-foreground">
                          {eur(Number(value))}
                        </span>
                      </span>
                    </div>
                  )}
                />
              }
            />
            <ChartLegend content={<ChartLegendContent />} />
            {chartType === 'bar'
              ? [
                  <Bar
                    key="subscriptionTotal"
                    dataKey="subscriptionTotal"
                    fill={trendConfig.subscriptionTotal.color}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={28}
                  />,
                  <Bar
                    key="consumptionTotal"
                    dataKey="consumptionTotal"
                    fill={trendConfig.consumptionTotal.color}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={28}
                  />,
                ]
              : [
                  <Line
                    key="subscriptionTotal"
                    type="monotone"
                    dataKey="subscriptionTotal"
                    stroke={trendConfig.subscriptionTotal.color}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: trendConfig.subscriptionTotal.color }}
                    activeDot={{
                      r: 5,
                      fill: trendConfig.subscriptionTotal.color,
                      stroke: '#fff',
                      strokeWidth: 2,
                    }}
                  />,
                  <Line
                    key="consumptionTotal"
                    type="monotone"
                    dataKey="consumptionTotal"
                    stroke={trendConfig.consumptionTotal.color}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: trendConfig.consumptionTotal.color }}
                    activeDot={{
                      r: 5,
                      fill: trendConfig.consumptionTotal.color,
                      stroke: '#fff',
                      strokeWidth: 2,
                    }}
                  />,
                ]}
          </ComposedChart>
        </ChartContainer>
      </div>
    </motion.div>
  );
}

function BillingCards({
  data,
  monthLabel,
}: {
  data: LineBillingDetail;
  monthLabel: string;
}) {
  const forfaitRows = data.subscription.filter((r) => r.type === 'ABONNEMENT');
  const materielRows = data.subscription.filter(
    (r) => r.type === 'FRAIS_PONCTUEL'
  );
  const forfaitTotal = sumAmount(forfaitRows);
  const materielTotal = sumAmount(materielRows);
  const invoiceTotal = data.subscriptionTotal + data.consumptionTotal;

  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3"
    >
      <TotalInvoiceCard
        total={invoiceTotal}
        subscriptionTotal={data.subscriptionTotal}
        consumptionTotal={data.consumptionTotal}
        monthLabel={monthLabel}
      />
      <SubscriptionCard
        total={data.subscriptionTotal}
        forfaitRows={forfaitRows}
        forfaitTotal={forfaitTotal}
        materielRows={materielRows}
        materielTotal={materielTotal}
        monthLabel={monthLabel}
      />
      <ConsumptionCard
        rows={data.consumption}
        total={data.consumptionTotal}
        monthLabel={monthLabel}
      />
    </motion.div>
  );
}

/** Card shell: small label, big amount, month, then breakdown content. */
function MetricCard({
  title,
  amount,
  amountClassName,
  monthLabel,
  children,
}: {
  title: string;
  amount: number;
  amountClassName?: string;
  monthLabel: string;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      variants={fadeUp}
      className="flex flex-col rounded-2xl bg-white p-6 shadow-sm"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {title}
      </p>
      <p
        className={cn(
          'mt-1 text-2xl font-bold tabular-nums text-slate-900',
          amountClassName
        )}
      >
        {eur(amount)}
      </p>
      <p className="mt-0.5 text-xs text-slate-400 capitalize">{monthLabel}</p>
      <div className="mt-5">{children}</div>
    </motion.div>
  );
}

/** Card 1 — « Total facture » split into abonnement (emerald) / hors-forfait (sky). */
function TotalInvoiceCard({
  total,
  subscriptionTotal,
  consumptionTotal,
  monthLabel,
}: {
  total: number;
  subscriptionTotal: number;
  consumptionTotal: number;
  monthLabel: string;
}) {
  return (
    <MetricCard title="Total facture" amount={total} monthLabel={monthLabel}>
      <div className="space-y-4">
        <ProportionRow
          label="Abonnement"
          amount={subscriptionTotal}
          total={total}
          barClassName="bg-emerald-500"
          dotClassName="bg-emerald-500"
        />
        <ProportionRow
          label="Hors forfait"
          amount={consumptionTotal}
          total={total}
          barClassName="bg-sky-500"
          dotClassName="bg-sky-500"
        />
      </div>
    </MetricCard>
  );
}

function ProportionRow({
  label,
  amount,
  total,
  barClassName,
  dotClassName,
}: {
  label: string;
  amount: number;
  total: number;
  barClassName: string;
  dotClassName: string;
}) {
  const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 text-slate-600">
          <span className={cn('h-2 w-2 rounded-full', dotClassName)} />
          {label}
        </span>
        <span className="font-medium tabular-nums text-slate-900">
          {eur(amount)}
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
          <div
            className={cn('h-full rounded-full', barClassName)}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="w-9 shrink-0 text-right text-xs tabular-nums text-slate-400">
          {pct}%
        </span>
      </div>
    </div>
  );
}

/** Card 2 — « Total abonnement » itemised into forfait + matériel sections. */
function SubscriptionCard({
  total,
  forfaitRows,
  forfaitTotal,
  materielRows,
  materielTotal,
  monthLabel,
}: {
  total: number;
  forfaitRows: LineSubscriptionBillingRow[];
  forfaitTotal: number;
  materielRows: LineSubscriptionBillingRow[];
  materielTotal: number;
  monthLabel: string;
}) {
  const empty = forfaitRows.length === 0 && materielRows.length === 0;
  return (
    <MetricCard
      title="Total abonnement"
      amount={total}
      amountClassName="text-emerald-600"
      monthLabel={monthLabel}
    >
      {empty ? (
        <p className="py-6 text-center text-sm text-slate-400">
          Aucun abonnement facturé ce mois-ci
        </p>
      ) : (
        <div className="space-y-5">
          <SubscriptionSection
            title="Forfait"
            rows={forfaitRows}
            subtotal={forfaitTotal}
          />
          <SubscriptionSection
            title="Matériel"
            rows={materielRows}
            subtotal={materielTotal}
          />
        </div>
      )}
    </MetricCard>
  );
}

function SubscriptionSection({
  title,
  rows,
  subtotal,
}: {
  title: string;
  rows: LineSubscriptionBillingRow[];
  subtotal: number;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </h3>
        <span className="text-sm font-semibold tabular-nums text-slate-900">
          {eur(subtotal)}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="mt-2 text-xs text-slate-400">—</p>
      ) : (
        <ul className="mt-2 divide-y divide-slate-100">
          {rows.map((row, i) => (
            <li
              key={i}
              className="flex items-start justify-between gap-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-slate-900">{row.label}</p>
                <p className="mt-0.5 text-xs text-slate-400">{row.period}</p>
              </div>
              <span className="shrink-0 text-sm font-medium tabular-nums text-slate-900">
                {eur(row.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Card 3 — « Total hors forfait », one line per consumption item. */
function ConsumptionCard({
  rows,
  total,
  monthLabel,
}: {
  rows: LineConsumptionBillingRow[];
  total: number;
  monthLabel: string;
}) {
  return (
    <MetricCard
      title="Total hors forfait"
      amount={total}
      amountClassName="text-sky-600"
      monthLabel={monthLabel}
    >
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          Aucun hors-forfait facturé ce mois-ci
        </p>
      ) : (
        <div>
          <div className="flex items-center justify-between pb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Détail ({rows.length})
            </h3>
          </div>
          <ul className="max-h-[260px] divide-y divide-slate-100 overflow-auto">
            {rows.map((row, i) => (
              <li
                key={i}
                className="flex items-start justify-between gap-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-900">
                    {row.productLabel}
                  </p>
                  <p className="mt-0.5 text-xs tabular-nums text-slate-400">
                    {formatDay(row.date)} ·{' '}
                    {formatConsumptionValue(row.unit, row.value)}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-medium tabular-nums text-slate-900">
                  {eur(row.amount)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </MetricCard>
  );
}
